import { useState, useEffect, createContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Marketplace from "@/pages/Marketplace";
import MyServices from "@/pages/MyServices";
import Transactions from "@/pages/Transactions";
import Requests from "@/pages/Requests";
import ActiveTransactions from "@/pages/ActiveTransactions";
import Notifications from "@/pages/Notifications";
import Messages from "@/pages/Messages";
import Profile from "@/pages/Profile";
import PublicProfile from "@/pages/PublicProfile";
import AdminDashboard from "@/pages/AdminDashboard";
import BlockchainWallet from "@/pages/BlockchainWallet";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppContext = createContext();

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const previousUnreadMessages = useRef(0);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUserBalance();
    } else {
      setLoading(false);
    }
  }, []);

  // Global message notification polling (only when not on messages page)
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkNewMessages = async () => {
      try {
        const response = await axios.get(`${API}/user/balance`);
        const currentUnread = response.data.unread_messages || 0;
        
        // Show notification if unread count increased
        if (currentUnread > previousUnreadMessages.current && previousUnreadMessages.current > 0) {
          const newMessageCount = currentUnread - previousUnreadMessages.current;
          toast(
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="w-5 h-5 text-blue-600 animate-pulse" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">New Message{newMessageCount > 1 ? 's' : ''}</p>
                <p className="text-sm text-gray-600 mt-1">
                  You have {newMessageCount} new message{newMessageCount > 1 ? 's' : ''}
                </p>
              </div>
            </div>,
            {
              duration: 5000,
              className: 'bg-blue-50 border-blue-200',
            }
          );
        }
        
        previousUnreadMessages.current = currentUnread;
        
        setUser(prev => ({
          ...prev,
          unread_messages: currentUnread,
          unread_notifications: response.data.unread_notifications || 0
        }));
      } catch (error) {
        console.error('Error checking new messages:', error);
      }
    };

    // Initialize the count
    if (user?.unread_messages !== undefined) {
      previousUnreadMessages.current = user.unread_messages;
    }

    // Poll every 15 seconds
    const interval = setInterval(checkNewMessages, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, API]);

  const fetchUserBalance = async () => {
    try {
      const response = await axios.get(`${API}/user/balance`);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Error fetching user balance:", error);
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser({ 
      id: userData.id,
      username: userData.username, 
      time_tokens: userData.time_tokens,
      escrow_tokens: userData.escrow_tokens || 0,
      unread_notifications: userData.unread_notifications || 0,
      unread_messages: userData.unread_messages || 0
    });
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateBalance = async () => {
    try {
      const response = await axios.get(`${API}/user/balance`);
      setUser(prev => ({ 
        ...prev, 
        time_tokens: response.data.time_tokens,
        escrow_tokens: response.data.escrow_tokens || 0,
        unread_notifications: response.data.unread_notifications || 0,
        unread_messages: response.data.unread_messages || 0
      }));
    } catch (error) {
      console.error("Error updating balance:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ user, updateBalance, API }}>
      <div className="App">
        <Toaster position="top-right" />
        <BrowserRouter>
          <Routes>
            <Route
              path="/auth"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <AuthPage onLogin={handleLogin} API={API} />
                )
              }
            />
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Landing />
                )
              }
            />
            <Route
              path="/admin"
              element={<AdminDashboard />}
            />
            <Route
              path="/dashboard"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Dashboard />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/marketplace"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Marketplace />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/my-services"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <MyServices />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/blockchain"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <BlockchainWallet />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/transactions"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Transactions />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/requests"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Requests />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/active"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <ActiveTransactions />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/notifications"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Notifications />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/messages"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Messages />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/messages/:userId"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Messages />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/user/:username"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <PublicProfile />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/profile"
              element={
                isAuthenticated ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Profile />
                  </Layout>
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
    </AppContext.Provider>
  );
}

export default App;